import { Mail, Phone as PhoneIcon, Trash2, ShieldCheck, Clock } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EMAIL, PHONE } from "@/data/mock";

const steps = [
  {
    icon: Mail,
    title: "Email us",
    desc: "Send an account deletion request to our support email from the email address linked to your Blitzwash account.",
  },
  {
    icon: PhoneIcon,
    title: "Or call us",
    desc: "Prefer to talk? Call us and our team will verify your identity and process the request.",
  },
  {
    icon: ShieldCheck,
    title: "We verify your identity",
    desc: "To protect your account, we confirm your registered phone number or email before deleting any data.",
  },
  {
    icon: Trash2,
    title: "Your account is deleted",
    desc: "Once verified, your personal data is permanently removed, subject to any legal retention requirements.",
  },
];

export default function DeleteAccountPage() {
  const phoneHref = `tel:${PHONE.replace(/\s/g, "")}`;
  const emailSubject = encodeURIComponent("Account Deletion Request");
  const emailBody = encodeURIComponent(
    "Hi Blitzwash,\n\nI would like to request the deletion of my Blitzwash account and associated data.\n\nRegistered name:\nRegistered phone number:\nRegistered email:\n\nThank you."
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1">
      {/* Hero */}
      <section className="pt-28 lg:pt-32 pb-10 bg-gradient-to-br from-sky-50/60 via-white to-lime-50/40 text-center">
        <div className="max-w-3xl mx-auto px-5">
          <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-3">
            Account
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            Delete Your Account
          </h1>
          <p className="text-slate-500 leading-relaxed">
            You can request deletion of your Blitzwash account and personal data at any time.
            Just reach out to us by email or phone and we'll take care of the rest.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 space-y-10">
          {/* Contact card */}
          <div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-4">
              How to request deletion
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              To delete your account, contact us using one of the options below. Please reach out from,
              or mention, the phone number and email registered with your account so we can verify your request.
            </p>

            <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-6 space-y-5">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Email</p>
                <a
                  href={`mailto:${EMAIL}?subject=${emailSubject}&body=${emailBody}`}
                  className="inline-flex items-center gap-2 text-base font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {EMAIL}
                </a>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Phone</p>
                <a
                  href={phoneHref}
                  className="inline-flex items-center gap-2 text-base font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                >
                  <PhoneIcon className="w-4 h-4" />
                  {PHONE}
                </a>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-4">
              What happens next
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {steps.map((step, i) => (
                <div key={i} className="bg-white rounded-3xl border border-slate-200 p-5">
                  <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center mb-4">
                    <step.icon className="w-5 h-5 text-sky-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 leading-relaxed">
                Requests are typically processed within 7 working days. Some information may be retained
                where required to comply with legal, tax, or fraud-prevention obligations, as described in
                our Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  );
}
